import React from 'react';
import { motion } from 'framer-motion';

interface Experience {
  title: string;
  company: string;
  period: string;
  description: string[];
  technologies: string[];
}

const experiences: Experience[] = [
  {
    title: "Software Development Intern",
    company: "Zipbooks Software Solutions",
    period: "Dec'24-Jul'25",
    description: [
      "Built scalable backend with FastAPI, PostgreSQL, and OOP, reducing database load by 30% via microservices, Redis caching, and B+ Tree indexing",
      "Optimized queries and concurrency, cutting API latency by 35% and scaling to 50K+ concurrent requests in a distributed system"
    ],
    technologies: ["FastAPI", "PostgreSQL", "Redis", "Microservices", "OOP", "Python", "Distributed Systems"]
  },
  {
    title: "AI/ML Intern",
    company: "Zipbooks Software Solutions",
    period: "Aug'21-Aug'25",
    description: [
      "Engineered OCR parser extracting payload from resumes, improving document throughput efficiency by 36%",
      "Applied prompt tuning and model optimization in LLM pipelines, boosting job relevance accuracy by 25%",
      "Deployed a scalable ML pipeline with a FastAPI dashboard on AWS, reducing classification latency by 44%"
    ],
    technologies: ["OCR", "LLMs", "FastAPI", "AWS", "Prompt Engineering", "Python", "Machine Learning"]
  },
  {
    title: "Research Assistant",
    company: "Pandit Deendayal Energy University",
    period: "Jan'23-May'24",
    description: [
      "Designed multimodal sarcasm model aligning MiniLM & Emoji2Vec embeddings in shared latent space, improving F1-score by 12%",
      "Architectured emoji-aware fusion mechanism combining semantic and emotive signals, boosting precision by 10% and recall by 9%",
      "Executed hyperparameter tuning and learning-rate annealing, improving convergence stability and achieving 85% accuracy"
    ],
    technologies: ["PyTorch", "RoBERTa", "CNN-LSTM", "Transformers", "NLP", "Computer Vision", "Multimodal AI"]
  }
];

const Experience: React.FC = () => {
  return (
    <section id="experience" className="py-20 relative overflow-hidden">
      <div className="container mx-auto px-6 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-purple-500">Work Experience</span>
          </h2>
          <div className="w-20 h-1 bg-gradient-to-r from-blue-500 to-purple-500 mx-auto rounded-full"></div>
        </motion.div>

        <div className="relative">
          {/* Decorative weaving lines */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
              <path
                d="M0,20 Q25,0 50,20 T100,20"
                stroke="rgba(var(--primary-color-rgb), 0.1)"
                strokeWidth="0.5"
                fill="none"
                className="animate-draw"
              />
              <path
                d="M0,40 Q75,20 100,40"
                stroke="rgba(var(--primary-color-rgb), 0.1)"
                strokeWidth="0.5"
                fill="none"
                className="animate-draw"
              />
              <path
                d="M0,60 Q25,80 50,60 T100,60"
                stroke="rgba(var(--primary-color-rgb), 0.1)"
                strokeWidth="0.5"
                fill="none"
                className="animate-draw"
              />
              <path
                d="M0,80 Q75,60 100,80"
                stroke="rgba(var(--primary-color-rgb), 0.1)"
                strokeWidth="0.5"
                fill="none"
                className="animate-draw"
              />
            </svg>
          </div>

          {/* Timeline line */}
          <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-0.5 bg-gray-300"></div>

          <div className="space-y-16">
            {experiences.map((exp, index) => (
              <motion.div
                key={exp.company}
                initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                viewport={{ once: true }}
                className={`relative flex items-center ${
                  index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                }`}
              >
                {/* Timeline dot */}
                <div className="absolute left-1/2 transform -translate-x-1/2 w-4 h-4 bg-primary-color rounded-full z-10 shadow-lg"></div>

                {/* Thread connectors */}
                {index < experiences.length - 1 && (
                  <>
                    <div className={`absolute left-1/2 transform -translate-x-1/2 w-0.5 h-32 bg-gradient-to-b from-primary-color/50 to-transparent ${
                      index % 2 === 0 ? 'top-full' : 'bottom-full'
                    }`}></div>
                    <div className={`absolute left-1/2 transform -translate-x-1/2 w-0.5 h-32 bg-gradient-to-b from-transparent to-primary-color/50 ${
                      index % 2 === 0 ? 'bottom-full' : 'top-full'
                    }`}></div>
                  </>
                )}

                {/* Content */}
                <div className={`w-full md:w-1/2 ${
                  index % 2 === 0 ? 'md:pr-8' : 'md:pl-8'
                }`}>
                  <div className="bg-white rounded-2xl p-8 shadow-xl border border-gray-200">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
                      <div>
                        <h3 className="text-2xl font-semibold text-gray-900 mb-2">{exp.title}</h3>
                        <p className="text-xl text-primary-color font-medium">{exp.company}</p>
                      </div>
                      <div className="mt-4 md:mt-0">
                        <span className="px-4 py-2 bg-gradient-to-r from-blue-400/20 to-purple-400/20 text-gray-700 border border-blue-300/30 rounded-full text-sm font-semibold">
                          {exp.period}
                        </span>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <ul className="list-none space-y-2 text-gray-700">
                        {exp.description.map((item, i) => (
                          <motion.li
                            key={i}
                            initial={{ opacity: 0, x: -20 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.5, delay: i * 0.1 }}
                            viewport={{ once: true }}
                            className="flex items-start"
                          >
                            <span className="inline-block w-2 h-2 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full mr-3 mt-2 flex-shrink-0 shadow-lg shadow-blue-500/50"></span>
                            <span>{item}</span>
                          </motion.li>
                        ))}
                      </ul>

                      <div className="flex flex-wrap gap-2 mt-6">
                        {exp.technologies.map((tech, i) => (
                          <motion.span
                            key={tech}
                            initial={{ opacity: 0, scale: 0.8 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.3, delay: i * 0.1 }}
                            viewport={{ once: true }}
                            className="px-3 py-1 bg-gray-200 text-gray-800 rounded-full text-sm font-medium shadow-sm"
                          >
                            {tech}
                          </motion.span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Experience;